import type { QueryClient } from "@tanstack/react-query";
import { CACHE_REGISTRY, type AffectedKey, type TableName } from "./registry";

/**
 * Invalider alle queryKeys som leser fra `table`, basert på CACHE_REGISTRY.
 *
 * - Globale entries (uten entityField) invalideres direkte med sin key.
 * - Entity-spesifikke entries forsøker å bruke context[entityField] som ID;
 *   hvis ID mangler faller vi tilbake til bred prefix-invalidering (React
 *   Query matcher alle [key, *]-varianter da).
 *
 * Returnerer en Promise som resolves når alle invalideringer er trigget.
 */
export async function invalidateFor(
  qc: QueryClient,
  table: TableName,
  context?: Record<string, string | undefined | null>,
): Promise<void> {
  // `as const satisfies Registry` inferer presise tuple-typer per tabell
  // (f.eks. readonly [{ key: readonly ["my-tasks"] }]). Cast til den bredere
  // readonly AffectedKey[]-typen for å iterere uniformt — dette er en
  // permanent nødvendighet, ikke en midlertidig løsning for tomt registry.
  const entries = CACHE_REGISTRY[table] as readonly AffectedKey[];
  if (entries.length === 0) return;

  await Promise.all(
    entries.map((entry) => {
      if (!("entityField" in entry) || entry.entityField === undefined) {
        return qc.invalidateQueries({ queryKey: entry.key });
      }
      const id = context?.[entry.entityField];
      return qc.invalidateQueries({
        queryKey: id ? [...entry.key, id] : entry.key,
      });
    }),
  );
}
